// src/useCases/parlayUseCases.ts
import { TrackerRepo } from '../services/trackerRepo';
import { BasePick, Parlay, ParlayStatus, PickStatus } from '../domain/types';
import { buildParlay, validateParlay } from '../services/parlayEngine';
import { createStakeTx, createPayoutTx, applyTx } from '../services/bankrollEngine';

export async function createParlayAndTrack(
  picks: BasePick[],
  stake: number,
  repo: TrackerRepo
): Promise<Parlay> {
  const validation = validateParlay(picks);
  if (!validation.ok) throw new Error(validation.error);

  const parlay = buildParlay(picks, stake);

  // 1) salva parlay no tracker
  await repo.upsert({ type: 'parlay', data: parlay });

  // 2) debita stake do bankroll (openStake)
  const bank = await repo.getBankroll();
  if (bank.current < stake) throw new Error('Saldo insuficiente');

  const stakeTx = createStakeTx('parlay', parlay.id, stake, `Parlay ${parlay.picks.length} pernas`);
  const newBank = applyTx(bank, stakeTx);
  await repo.setBankroll(newBank);
  await repo.addTx(stakeTx);

  return parlay;
}

export async function addPickToTracker(
  pick: BasePick & { stake: number; status?: PickStatus },
  repo: TrackerRepo
) {
  const bank = await repo.getBankroll();
  if (bank.current < pick.stake) throw new Error('Saldo insuficiente');

  await repo.upsert({ type: 'pick', data: { ...pick, status: PickStatus.PENDING } });

  const stakeTx = createStakeTx('pick', pick.id, pick.stake, `${pick.player} - ${pick.market}`);
  const newBank = applyTx(bank, stakeTx);
  await repo.setBankroll(newBank);
  await repo.addTx(stakeTx);
}

// ── Resolução automática ──
export type FetchStatFn = (pick: BasePick) => Promise<number>;

export async function resolvePending(
  repo: TrackerRepo,
  fetchRealStat: FetchStatFn
) {
  const entries = await repo.getAll();
  const pending = entries.filter(e =>
    e.type === 'parlay' ? e.data.status === ParlayStatus.PENDING
                        : e.data.status === 'pending'
  );

  for (const entry of pending) {
    if (entry.type === 'pick') {
      if (entry.data.status === PickStatus.PENDING) await resolvePick(entry.data, repo, fetchRealStat);
    } else {
      await resolveParlay(entry.data, repo, fetchRealStat);
    }
  }
}

async function resolvePick(
  pick: BasePick & { stake: number; status: PickStatus },
  repo: TrackerRepo,
  fetchRealStat: FetchStatFn
) {
  const real = await fetchRealStat(pick);
  const { status, pnl } = settlePick(pick, real);

  const updated = { ...pick, status, resultValue: real, settledAt: new Date().toISOString() };
  await repo.upsert({ type: 'pick', data: updated });

  if (pnl !== 0) {
    const bank = await repo.getBankroll();
    const payoutTx = createPayoutTx('pick', pick.id, pnl, status === 'won' ? 'Green' : 'Red');
    const newBank = applyTx(bank, payoutTx);
    await repo.setBankroll(newBank);
    await repo.addTx(payoutTx);
  }
}

async function resolveParlay(
  parlay: Parlay,
  repo: TrackerRepo,
  fetchRealStat: FetchStatFn
) {
  const legResults = new Map<string, { won: boolean; push: boolean }>();
  for (const leg of parlay.picks) {
    const real = await fetchRealStat(leg);
    const won = leg.side === 'over' ? real > leg.line : real < leg.line;
    const push = real === leg.line;
    legResults.set(leg.id, { won, push });
  }

  const { status, pnl, settledAt } = settleParlay(parlay, legResults);

  const updated = { ...parlay, status, resolvedAt: settledAt };
  await repo.upsert({ type: 'parlay', data: updated });

  if (pnl !== 0) {
    const bank = await repo.getBankroll();
    const payoutTx = createPayoutTx('parlay', parlay.id, pnl, status === ParlayStatus.GREEN ? 'Green' : 'Red');
    const newBank = applyTx(bank, payoutTx);
    await repo.setBankroll(newBank);
    await repo.addTx(payoutTx);
  }
}

// re-export para uso interno
import { settlePick } from '../services/bankrollEngine';
import { settleParlay } from '../services/bankrollEngine';
